import React, { useEffect, useState, useCallback } from 'react';
import { FiCpu, FiCheckCircle, FiTarget, FiActivity, FiRefreshCw } from 'react-icons/fi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Card, StatCard, Button } from '../../components/UI';
import { getTrainingReadiness, getAgreementMetric } from '../../services/adminService';

// Progress bar for a single training-data track.
const TrackBar = ({ label, track }) => {
  const pct = track?.pct ?? 0;
  const ready = !!track?.ready;
  const barColor = ready ? '#10b981' : '#0ea5e9';
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontWeight: 600, color: '#1e293b' }}>{label}</span>
        <span style={{ color: '#64748b', fontWeight: 600 }}>
          {track?.count == null ? '—' : `${track.count.toLocaleString()} / ${track.target.toLocaleString()}`}
        </span>
      </div>
      <div style={{ height: 12, borderRadius: 8, background: '#e2e8f0', overflow: 'hidden' }}>
        <div
          style={{
            height: 12,
            width: `${pct}%`,
            background: barColor,
            borderRadius: 8,
            transition: 'width .4s ease',
          }}
        />
      </div>
      <div style={{ marginTop: 6, fontSize: 13, color: ready ? '#10b981' : '#64748b' }}>
        {track?.count == null
          ? 'Could not load'
          : ready
          ? 'Ready to train ✓'
          : `${track.remaining.toLocaleString()} to go`}
      </div>
    </div>
  );
};

const AiTraining = () => {
  const [readiness, setReadiness] = useState(null);
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [r, a] = await Promise.all([
        getTrainingReadiness().catch((e) => ({ __err: e.message })),
        getAgreementMetric().catch((e) => ({ __err: e.message })),
      ]);
      if (r?.__err && a?.__err) throw new Error(r.__err);
      setReadiness(r?.__err ? null : r);
      setAgreement(a?.__err ? null : a);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const agreePct =
    agreement?.agreementRate != null ? `${Math.round(agreement.agreementRate * 100)}%` : '—';

  const chartData = agreement?.byMonth
    ? Object.keys(agreement.byMonth)
        .sort()
        .map((m) => {
          const b = agreement.byMonth[m];
          const judged = (b.agree || 0) + (b.partial || 0) + (b.disagree || 0);
          return {
            month: m,
            agreement: judged ? Math.round(((b.agree + 0.5 * b.partial) / judged) * 100) : 0,
          };
        })
    : [];

  return (
    <div style={{ padding: '8px 4px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, color: '#0f172a' }}>AI Training</h1>
          <p style={{ margin: '4px 0 0', color: '#64748b' }}>
            Health Trajectory — data collected toward the first trained model.
          </p>
        </div>
        <Button onClick={load} disabled={loading}>
          <FiRefreshCw style={{ marginRight: 6 }} />
          Refresh
        </Button>
      </div>

      {error ? (
        <Card>
          <p style={{ color: '#ef4444' }}>
            {error.toLowerCase().includes('permission')
              ? 'Admin access required to view training stats.'
              : `Could not load training stats: ${error}`}
          </p>
        </Card>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 16,
              marginBottom: 20,
            }}
          >
            <StatCard
              title="Vet-confirmed labels"
              value={readiness?.labeledOutcomes?.count ?? 0}
              icon={FiCheckCircle}
              color="secondary"
            />
            <StatCard
              title="AI scans captured"
              value={readiness?.scans?.count ?? 0}
              icon={FiCpu}
              color="primary"
            />
            <StatCard title="AI-vs-vet agreement" value={agreePct} icon={FiActivity} color="warning" />
            <StatCard
              title="Total vet outcomes"
              value={readiness?.totalOutcomes ?? 0}
              icon={FiTarget}
              color="primary"
            />
          </div>

          <Card title="Training readiness">
            <TrackBar label="Vet-confirmed labels (supervised set)" track={readiness?.labeledOutcomes} />
            <TrackBar label="AI scans captured (feature corpus)" track={readiness?.scans} />
            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' }}>
              Targets are first-model estimates. The learned model (replacing the cold-start
              heuristic) can be trained once the vet-confirmed label set reaches target.
            </p>
          </Card>

          <Card title="AI-vs-vet agreement over time (moat metric)" style={{ marginTop: 20 }}>
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="month" stroke="#64748b" />
                  <YAxis domain={[0, 100]} unit="%" stroke="#64748b" />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Line type="monotone" dataKey="agreement" stroke="#10b981" strokeWidth={2} dot />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ color: '#94a3b8' }}>
                No vet-confirmed outcomes yet. This chart fills in as vets confirm diagnoses — a
                rising line is the evidence the data flywheel is working.
              </p>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default AiTraining;
